import { useEffect, useState } from "react";
import { Save, ExternalLink, ShieldCheck, ShieldAlert, Mail, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/settings").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-gray-400">Loading…</div>;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings", {
        sender_email: data.sender_email,
        sender_name: data.sender_name,
        company_address: data.company_address,
        company_website: data.company_website,
      });
      setData(r.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Configuration</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Settings</h1>
        <p className="text-sm text-gray-500 mt-2">Email sender, domain verification, webhook security.</p>
      </div>

      {/* Pitch PDF download */}
      <div className="section-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-gradient-to-r from-[#004B87]/5 to-transparent" data-testid="pitch-pdf-section">
        <div className="w-12 h-12 grid place-items-center bg-[#004B87] text-white">
          <ExternalLink size={20} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold">Présentation Sodimate UAE Platform</div>
          <div className="text-sm text-gray-600 mt-1">
            PDF de présentation (8 pages) prêt à partager avec la direction Sodimate :
            contexte, fonctionnalités, bénéfices chiffrés, parcours type, technique &amp; roadmap.
          </div>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/pitch`}
          target="_blank"
          rel="noreferrer"
          className="btn-primary whitespace-nowrap"
          data-testid="download-pitch-pdf"
        >
          Télécharger le PDF
        </a>
      </div>

      {/* Email sender */}
      <div className="section-card p-6 space-y-4" data-testid="email-sender-section">
        <div className="flex items-center gap-3">
          <Mail size={18} className="text-[#004B87]" />
          <div>
            <div className="font-display text-lg font-bold">Email sender</div>
            <div className="text-xs text-gray-500">Used for prospecting emails sent via Resend.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">From email</label>
            <input className="input-stark" value={data.sender_email || ""}
              onChange={(e) => update("sender_email", e.target.value)}
              disabled={!isAdmin}
              data-testid="sender-email-input"
              placeholder="bd@your-domain.com" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Sender name</label>
            <input className="input-stark" value={data.sender_name || ""}
              onChange={(e) => update("sender_name", e.target.value)}
              disabled={!isAdmin}
              data-testid="sender-name-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-2">Company address (footer)</label>
            <input className="input-stark" value={data.company_address || ""}
              onChange={(e) => update("company_address", e.target.value)}
              disabled={!isAdmin} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-2">Company website (footer)</label>
            <input className="input-stark" value={data.company_website || ""}
              onChange={(e) => update("company_website", e.target.value)}
              disabled={!isAdmin} />
          </div>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2 pt-2">
          <span className={`chip ${data.resend_configured ? "border-[#059669] text-[#059669] bg-emerald-50" : "border-red-400 text-red-600 bg-red-50"}`}>
            {data.resend_configured ? "Resend API configured" : "Resend API missing"}
          </span>
          <span className={`chip ${data.webhook_signed ? "border-[#059669] text-[#059669] bg-emerald-50" : "border-amber-400 text-amber-700 bg-amber-50"}`}>
            {data.webhook_signed ? "Webhook signed" : "Webhook unsigned"}
          </span>
        </div>

        {isAdmin && (
          <div className="pt-2 flex justify-end">
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-settings-button">
              <Save size={14} /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {/* Domain verification guide */}
      <div className="section-card p-6 space-y-3" data-testid="domain-guide">
        <div className="flex items-center gap-3">
          <Globe size={18} className="text-[#004B87]" />
          <div>
            <div className="font-display text-lg font-bold">Verify a sending domain in Resend</div>
            <div className="text-xs text-gray-500">Required to send to real prospects (not just your Resend account email).</div>
          </div>
        </div>

        <ol className="space-y-2 text-sm text-gray-700 list-decimal pl-5">
          <li>Open <a className="text-[#004B87] underline inline-flex items-center gap-1" href="https://resend.com/domains" target="_blank" rel="noreferrer">Resend → Domains <ExternalLink size={11} /></a> and click <span className="font-mono bg-gray-100 px-1">Add Domain</span>.</li>
          <li>Add your domain (e.g. <span className="font-mono bg-gray-100 px-1">mail.sodimate-uae.com</span>) — recommend a sub-domain to keep root mail unaffected.</li>
          <li>Resend will display 3 DNS records (SPF / DKIM / MX). Add them on your DNS provider (OVH, Cloudflare, Route53…).</li>
          <li>Wait 5–60 minutes, then click <span className="font-mono bg-gray-100 px-1">Verify DNS records</span> in Resend.</li>
          <li>Once <span className="text-[#059669] font-bold">Verified</span>, set the <b>From email</b> above to <span className="font-mono bg-gray-100 px-1">bd@mail.sodimate-uae.com</span> and click <b>Save</b>.</li>
        </ol>

        <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 p-3">
          <b>Test mode reminder:</b> Until a domain is verified, Resend only delivers to the email address you signed up with.
        </div>
      </div>

      {/* Webhook setup */}
      <div className="section-card p-6 space-y-3" data-testid="webhook-guide">
        <div className="flex items-center gap-3">
          {data.webhook_signed ? (
            <ShieldCheck size={18} className="text-[#059669]" />
          ) : (
            <ShieldAlert size={18} className="text-amber-600" />
          )}
          <div>
            <div className="font-display text-lg font-bold">Resend webhook (event tracking)</div>
            <div className="text-xs text-gray-500">Auto-log delivered / opened / clicked / replied events to company timelines.</div>
          </div>
        </div>

        <div>
          <div className="label-eyebrow mb-2">Webhook URL (paste into Resend)</div>
          <div className="font-mono text-xs bg-gray-100 border border-gray-200 p-3 break-all select-all" data-testid="webhook-url">
            {process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend
          </div>
        </div>

        <ol className="space-y-2 text-sm text-gray-700 list-decimal pl-5 mt-2">
          <li>Go to <a className="text-[#004B87] underline inline-flex items-center gap-1" href="https://resend.com/webhooks" target="_blank" rel="noreferrer">Resend → Webhooks <ExternalLink size={11} /></a>.</li>
          <li>Click <span className="font-mono bg-gray-100 px-1">Add Endpoint</span> and paste the URL above.</li>
          <li>Select events: <span className="font-mono bg-gray-100 px-1">email.delivered</span>, <span className="font-mono bg-gray-100 px-1">email.opened</span>, <span className="font-mono bg-gray-100 px-1">email.clicked</span>, <span className="font-mono bg-gray-100 px-1">email.bounced</span>, <span className="font-mono bg-gray-100 px-1">email.complained</span>.</li>
          <li>After creation, copy the <b>Signing Secret</b> (starts with <span className="font-mono bg-gray-100 px-1">whsec_</span>).</li>
          <li>Add it to <span className="font-mono bg-gray-100 px-1">/app/backend/.env</span> as <span className="font-mono bg-gray-100 px-1">RESEND_WEBHOOK_SECRET=whsec_…</span> then restart the backend.</li>
          <li>The <i>Webhook signed</i> badge above will turn green once the secret is loaded.</li>
        </ol>
      </div>
    </div>
  );
}
